import type { List, ListCreate, ListUpdate } from '../types/list'
import type { Elemental, OwnershipInformation } from '../types/elemental'
import type { APIIntervals, APIOrderings } from '../types/api'
import type { Prompt } from '../types/prompt'
import type { GetUserPromptMode } from './users'
import type { ElementalQuery } from './elementals'
import { APIFetch, filterResponse, filterResponseBoolean } from '.'
export interface PageQuery {
  page?: number
  size?: number
  order_dir?: 'asc' | 'desc'
  order_by?: APIOrderings
  interval?: APIIntervals
  search?: string
}
export interface TreeQuery {
  page?: number
  size?: number
}

export interface GetListsQuery extends PageQuery {
  topic_id?: number | string
  prompt_id?: string
  mode?: GetUserPromptMode
  is_premium?: boolean
  companiesSection?: boolean
  elemental_type_id?: number
}
export const getLists = async (queries?: GetListsQuery): Promise<null | List[]> => {
  let url = '/lists'

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const createList = async (body: ListCreate): Promise<null | List> => {
  return filterResponse(await APIFetch('/lists', {
    method: 'POST',
    body,
  }))
}

export const getList = async (listId: string): Promise<null | List> => {
  return filterResponse(await APIFetch(`/lists/${listId}`, {
    method: 'GET',
  }))
}

// Returns true if success
export const updateList = async (listId: string, body: ListUpdate): Promise<null | List> => {
  return filterResponse(await APIFetch(`/lists/${listId}`, {
    method: 'PATCH',
    body,
  }))
}

// Returns true if success
export const deleteList = async (listId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}`, {
    method: 'DELETE',
  }))
}
export const saveList = async (listId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/saves`, {
    method: 'POST',
  }))
}

export const removeSavedList = async (listId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/saves`, {
    method: 'DELETE',
  }))
}

export const uploadAvatar = async (listId: string, avatar: File, isAdmin?: boolean) => {
  const formData = new FormData()
  formData.append('file', avatar)

  return filterResponseBoolean(await APIFetch(`${isAdmin ? '/admin' : ''}/lists/${listId}/avatar`, {
    method: 'PATCH',
    body: formData,
  }))
}

export const uploadImage = async (listId: string, image: File, isAdmin?: boolean) => {
  const formData = new FormData()
  formData.append('file', image)

  return filterResponseBoolean(await APIFetch(`${isAdmin ? '/admin' : ''}/lists/${listId}/images`, {
    method: 'POST',
    body: formData,
  }))
}

export const deleteImage = async (listId: string, imageId: number, isAdmin?: boolean) => {
  return filterResponseBoolean(await APIFetch(`${isAdmin ? '/admin' : ''}/lists/${listId}/images/${imageId}`, {
    method: 'DELETE',
  }))
}

export const getListPrompts = async <T = Prompt>(
  listId: string,
  queries?: ElementalQuery & {
    mode?: string
    is_preview?: boolean
  },
  signal?: AbortSignal,
  isAdmin?: boolean,
): Promise<null | T[]> => {
  let url = `${isAdmin ? '/admin' : ''}/lists/${listId}/prompts`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const getListItems = async <T = Elemental>(
  listId: string | number,
  queries?: PageQuery & {
    mode?: string
    is_preview?: boolean
    item_type?: 'all' | 'list' | 'prompt' | 'folder' | 'no-folder'
  },
  signal?: AbortSignal,
  isAdmin?: boolean,
): Promise<null | T[]> => {
  let url = `${isAdmin ? '/admin' : ''}/lists/${listId}/items`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const getListTree = async <T = Elemental>(
  listId: string | number,
  queries?: TreeQuery,
  signal?: AbortSignal,
): Promise<null | T[]> => {
  let url = `/elementals/${listId}/tree`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const addListPrompt = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/prompts/${promptId}`, {
    method: 'POST',
  }))
}

export const removeListPrompt = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/prompts/${promptId}`, {
    method: 'DELETE',
  }))
}

export const getUserLists = async (userId: string, queries?: GetListsQuery, signal?: AbortSignal): Promise<List[] | null> => {
  let url = `/users/${userId}/lists`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const savePromptToList = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/prompts/${promptId}`, {
    method: 'POST',
  }))
}
export const removePromptFromList = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/prompts/${promptId}`, {
    method: 'DELETE',
  }))
}

export const voteList = async (listId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/votes`, {
    method: 'POST',
  }))
}

export const removeListVote = async (listId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/lists/${listId}/votes`, {
    method: 'DELETE',
  }))
}

export const getListOwnershipInformation = async (listId: string | number, isAdmin?: boolean): Promise<null | OwnershipInformation> => {
  return filterResponse(await APIFetch<OwnershipInformation>(`${isAdmin ? '/admin' : ''}/lists/${listId}/information`, {
    method: 'GET',
    baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
  }))
}

type UpdateListAsAdmin = { isHidden?: boolean; isFeatured?: boolean; companiesSection?: boolean } & ListUpdate
export const updateListAsAdmin = async (listIdOrUrl: string, body: UpdateListAsAdmin): Promise<List | null> => {
  return filterResponse(await APIFetch(`/admin/lists/${listIdOrUrl}`, {
    body,
    method: 'PATCH',
  }))
}
